import { PaginatedResource } from '../../typings/Paginated';
import { BaseService, GenericPaginatedParameters, Unauthorized, UnexpectedError, ClientError, PartialBy } from './base-service';

export interface GetDocumentCategoriesPaginatedParameters extends GenericPaginatedParameters {
    actingBodyUnitIds?: number[];
}

export class DocumentService extends BaseService {
    async getDocuments(params: GenericPaginatedParameters): Promise<PaginatedResource<DocumentFile>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/documents${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentById(id: number): Promise<DocumentFile> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentVersions(documentId: number): Promise<{ id: number; version: number; createdAt: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${documentId}/versions`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentDownloadUrl(documentId: number, version: number): Promise<string> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${documentId}/version/${version}/download`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        const { url } = await res.json();
        return url;
    }

    async createDocument(params: Omit<BaseDocument, 'id'> & { file: File; }): Promise<DocumentFile> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', params.file, params.file.name);
        formData.append('title', params.title);
        formData.append('documentCategoryId', String(params.documentCategoryId));
        formData.append('actingBodyUnitId', String(params.actingBodyUnitId));

        const res = await fetch(`${this.centralEndpoint}/document`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateDocument(id: number, file: File): Promise<DocumentFile> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', file, file.name);

        const res = await fetch(`${this.centralEndpoint}/document/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentCategories(params: GetDocumentCategoriesPaginatedParameters): Promise<PaginatedResource<DocumentCategory>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-categories${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getSimplifiedDocumentCategories(params: { actingBodyUnitId?: number; }): Promise<SimplifiedDocumentCategory[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/simplified-document-categories${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentCategoryById(id: number): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createDocumentCategory(params: Omit<BaseDocumentCategory, 'id'>): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateDocumentCategory(params: PartialBy<Omit<BaseDocumentCategory, 'actingBodyUnitId'>, 'id'>): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async deleteDocumentCategory(id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getActingBodies(): Promise<ActingBodySimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/documents/acting-bodies`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentUsers(params: { actingBodyId?: number; actingBodyUnitIds?: number[]; textFilter?: string; }): Promise<{ id: number; name: string; warName?: string | null; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-users${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }
}

export const documentService = new DocumentService();
